import { createFileRoute } from "@tanstack/react-router";
import {
	Check,
	ChevronRight,
	Copy,
	ExternalLink,
	Info,
	ListChecks,
	Plug,
	ShieldOff,
	X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useScrollEdgeMask } from "@/hooks/useScrollEdgeMask";
import { cn } from "@/utils/utils";

/** Canonical remote MCP resource — the one address every client pastes. */
const MCP_PUBLIC_URL = "https://mcp.ruralops.com/mcp";

const CURSOR_INSTALL_HREF =
	"https://cursor.com/install-mcp?name=ruralops&config=eyJ1cmwiOiJodHRwczovL21jcC5ydXJhbG9wcy5jb20vbWNwIn0=";

const CURSOR_CONFIG_JSON = `{
  "mcpServers": {
    "ruralops": {
      "url": "${MCP_PUBLIC_URL}"
    }
  }
}`;

const CLAUDE_CODE_COMMAND = `claude mcp add --transport http ruralops ${MCP_PUBLIC_URL}`;

const FEATURE_KEYS = [
	"farms",
	"counts",
	"animals",
	"records",
	"write",
] as const;

const LIMIT_KEYS = [
	"otherDomains",
	"noCost",
	"noDelete",
	"noSignup",
	"noPush",
] as const;

const CLIENT_KEYS = [
	"claude",
	"claudeCode",
	"cursor",
	"chatgpt",
	"grok",
] as const;

/** Which copy button is showing its "Copied" state — only ever one at a time. */
type CopyTarget = "addr" | "step" | "json";

const COPIED_RESET_MS = 1600;

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/mcp/")({
	component: McpPage,
});

/**
 * One shared copied-indicator for the whole page: the design asks for a single
 * confirmation at a time, so the state lives above the three copy buttons
 * rather than inside each of them.
 */
function useCopyIndicator() {
	const { t } = useTranslation();
	const [copied, setCopied] = useState<CopyTarget | null>(null);
	const timerRef = useRef<number | null>(null);

	useEffect(
		() => () => {
			if (timerRef.current !== null) window.clearTimeout(timerRef.current);
		},
		[],
	);

	const copy = async (target: CopyTarget, value: string) => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(target);
			if (timerRef.current !== null) window.clearTimeout(timerRef.current);
			timerRef.current = window.setTimeout(
				() => setCopied(null),
				COPIED_RESET_MS,
			);
		} catch {
			toast.error(t("mcp.url.copyFailed"));
		}
	};

	return { copied, copy };
}

function McpPage() {
	const { t } = useTranslation();
	const { copied, copy } = useCopyIndicator();

	return (
		<div
			data-testid="mcp-page"
			className="mx-auto flex w-full max-w-[920px] flex-col gap-6 pb-16 sm:gap-8"
		>
			<header className="flex flex-col gap-4 sm:gap-5">
				<p className="font-display font-bold text-base-content/60 text-xs uppercase tracking-[0.14em]">
					{t("mcp.page.eyebrow")}
				</p>
				{/* The subtitle runs to four lines on a phone, and centring against it
				    left the mark floating halfway down a tall text column. Top-aligned
				    below `sm` so the icon sits on the title line; centred again from
				    `sm` up, where the subtitle is one line. */}
				<div className="flex items-start gap-3 sm:items-center sm:gap-4">
					<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 sm:h-12 sm:w-12">
						<Plug
							className="h-5 w-5 text-primary sm:h-6 sm:w-6"
							strokeWidth={1.75}
						/>
					</span>
					<div className="flex min-w-0 flex-col gap-1">
						<h1
							data-testid="mcp-title"
							className="font-display font-extrabold text-2xl text-base-content leading-tight sm:text-3xl"
						>
							{t("mcp.page.title")}
						</h1>
						<p className="text-base-content/70 text-base">
							{t("mcp.page.subtitle")}
						</p>
					</div>
				</div>
				<p className="text-base text-base-content/90 leading-relaxed">
					{t("mcp.page.intro")}
				</p>
			</header>

			<AddressCard copied={copied === "addr"} onCopy={copy} />

			<section className="flex flex-col gap-4">
				<h2 className="font-display font-bold text-base-content text-xl">
					{t("mcp.connect.title")}
				</h2>
				<ClientTabs copied={copied} onCopy={copy} />

				<div className="flex items-start gap-3 rounded-lg border border-info/25 bg-info/10 px-3.5 py-3 sm:px-4 sm:py-3.5">
					<Info
						className="mt-0.5 h-4 w-4 shrink-0 text-info"
						strokeWidth={1.75}
					/>
					<p className="text-base-content/90 text-sm leading-relaxed">
						{t("mcp.connect.after")}
					</p>
				</div>
			</section>

			<div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
				<PermissionCard
					icon={
						<ListChecks className="h-5 w-5 text-primary" strokeWidth={1.75} />
					}
					title={t("mcp.features.title")}
				>
					{FEATURE_KEYS.map((key) => (
						<li key={key} className="flex gap-2.5">
							<Check
								className="mt-0.5 h-4 w-4 shrink-0 text-primary"
								strokeWidth={1.75}
							/>
							<span>{t(`mcp.features.${key}`)}</span>
						</li>
					))}
				</PermissionCard>

				<PermissionCard
					icon={
						<ShieldOff
							className="h-5 w-5 text-base-content/55"
							strokeWidth={1.75}
						/>
					}
					title={t("mcp.limits.title")}
				>
					{LIMIT_KEYS.map((key) => (
						<li key={key} className="flex gap-2.5">
							<X
								className="mt-0.5 h-4 w-4 shrink-0 text-base-content/45"
								strokeWidth={1.75}
							/>
							<span>{t(`mcp.limits.${key}`)}</span>
						</li>
					))}
				</PermissionCard>
			</div>
		</div>
	);
}

function AddressCard({
	copied,
	onCopy,
}: {
	copied: boolean;
	onCopy: (target: CopyTarget, value: string) => void;
}) {
	const { t } = useTranslation();

	return (
		<div
			data-testid="mcp-url-block"
			className="flex flex-col gap-3 rounded-xl border border-base-300 bg-base-100 px-4 py-4 shadow-sm sm:px-6 sm:py-5"
		>
			<p className="font-display font-semibold text-base-content/60 text-sm">
				{t("mcp.url.label")}
			</p>
			{/* Stacked on a phone: wrapping left the copy button as a stray chip
			    under a full-width field. It reads as the card's action at full
			    width, and returns to the field's right from `sm` up. */}
			<div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center">
				<code
					data-testid="mcp-url"
					className="flex h-11 min-w-0 flex-1 items-center truncate rounded-lg border border-base-300 bg-base-200 px-3 font-mono text-base-content text-sm sm:px-4"
				>
					{MCP_PUBLIC_URL}
				</code>
				<Button
					type="button"
					size="touch"
					className="w-full sm:w-auto"
					data-testid="mcp-copy-url"
					onClick={() => onCopy("addr", MCP_PUBLIC_URL)}
				>
					{copied ? (
						<Check className="h-4 w-4" />
					) : (
						<Copy className="h-4 w-4" />
					)}
					<span>{copied ? t("mcp.url.copied") : t("mcp.url.copy")}</span>
				</Button>
			</div>
		</div>
	);
}

function ClientTabs({
	copied,
	onCopy,
}: {
	copied: CopyTarget | null;
	onCopy: (target: CopyTarget, value: string) => void;
}) {
	const { t } = useTranslation();
	// Five client names are wider than a phone viewport. Left unscrolled the
	// strip pushed a horizontal scrollbar onto the whole page and clipped Grok
	// Bot unreachably; the hook adds the edge fade and mouse-wheel reach the
	// hidden scrollbar takes away.
	const { ref: stripRef, maskImage } = useScrollEdgeMask<HTMLDivElement>();

	/**
	 * Every tab's first step offers the same address, and only one panel is ever
	 * mounted — so they share a single copy target instead of one per client.
	 */
	const addressField = (
		<CopyField
			value={MCP_PUBLIC_URL}
			copied={copied === "step"}
			onCopy={() => onCopy("step", MCP_PUBLIC_URL)}
		/>
	);

	return (
		<Tabs defaultValue="claude">
			<div
				ref={stripRef}
				className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
				style={{ maskImage, WebkitMaskImage: maskImage }}
			>
				{/* inline-flex + min-w-full: sizes to its content so the parent can
				    scroll, while the hairline still spans the row when it fits. */}
				<TabsList className="inline-flex h-auto min-w-full justify-start gap-5 rounded-none border-base-300 border-b bg-transparent p-0 sm:gap-6">
					{CLIENT_KEYS.map((key) => (
						<TabsTrigger
							key={key}
							value={key}
							data-testid={`mcp-tab-${key}`}
							className={cn(
								"relative shrink-0 rounded-none bg-transparent px-0 pt-3 pb-3.5 font-display font-semibold text-base-content/60 text-[0.9375rem] shadow-none transition-colors",
								"after:absolute after:inset-x-0 after:-bottom-px after:h-[2.5px] after:rounded-sm after:bg-transparent after:transition-colors",
								"data-[state=active]:bg-transparent data-[state=active]:text-base-content data-[state=active]:shadow-none data-[state=active]:after:bg-primary",
							)}
						>
							{t(`mcp.connect.${key}.name`)}
						</TabsTrigger>
					))}
				</TabsList>
			</div>

			<TabsContent value="claude" className="mt-4">
				<StepGrid>
					<Step number={1} client="claude" step="step1">
						{addressField}
					</Step>
					<Step number={2} client="claude" step="step2">
						<PathChips
							steps={[
								t("mcp.connect.claude.pathSettings"),
								t("mcp.connect.claude.pathConnectors"),
								t("mcp.connect.claude.pathAddConnector"),
							]}
						/>
					</Step>
					<Step number={3} client="claude" step="step3" />
				</StepGrid>
			</TabsContent>

			<TabsContent value="claudeCode" className="mt-4">
				<StepGrid>
					<Step number={1} client="claudeCode" step="step1">
						<CopyField
							value={CLAUDE_CODE_COMMAND}
							copied={copied === "step"}
							onCopy={() => onCopy("step", CLAUDE_CODE_COMMAND)}
						/>
					</Step>
					<Step number={2} client="claudeCode" step="step2" />
					<Step number={3} client="claudeCode" step="step3" />
				</StepGrid>
			</TabsContent>

			<TabsContent value="cursor" className="mt-4">
				<div className="flex flex-col gap-4">
					<StepGrid>
						<Step number={1} client="cursor" step="step1">
							{addressField}
						</Step>
						<Step number={2} client="cursor" step="step2">
							<div className="flex flex-col items-start gap-3">
								<PathChips
									steps={[
										t("mcp.connect.cursor.pathSettings"),
										t("mcp.connect.cursor.pathTools"),
										t("mcp.connect.cursor.pathNewServer"),
									]}
								/>
								<Button variant="outline" size="sm" asChild>
									<a
										href={CURSOR_INSTALL_HREF}
										data-testid="mcp-add-cursor"
										target="_blank"
										rel="noreferrer"
									>
										<ExternalLink className="h-4 w-4" />
										{t("mcp.url.addToCursor")}
									</a>
								</Button>
							</div>
						</Step>
						<Step number={3} client="cursor" step="step3" />
					</StepGrid>
					<CodeBlock
						label={t("mcp.connect.copyConfig")}
						value={CURSOR_CONFIG_JSON}
						copied={copied === "json"}
						onCopy={() => onCopy("json", CURSOR_CONFIG_JSON)}
					/>
				</div>
			</TabsContent>

			<TabsContent value="chatgpt" className="mt-4">
				<StepGrid>
					<Step number={1} client="chatgpt" step="step1">
						{addressField}
					</Step>
					<Step number={2} client="chatgpt" step="step2" />
					<Step number={3} client="chatgpt" step="step3" />
				</StepGrid>
			</TabsContent>

			<TabsContent value="grok" className="mt-4">
				<StepGrid>
					<Step number={1} client="grok" step="step1">
						{addressField}
					</Step>
					<Step number={2} client="grok" step="step2" />
					<Step number={3} client="grok" step="step3" />
				</StepGrid>
			</TabsContent>
		</Tabs>
	);
}

/**
 * The three steps of a tab, side by side on a wide viewport and stacked below
 * it. Hairlines come from the 1px gap showing the container's own background,
 * so the dividers never double up at the wrap point.
 */
function StepGrid({ children }: { children: ReactNode }) {
	return (
		<div className="grid gap-px overflow-hidden rounded-xl border border-base-300 bg-base-300 sm:grid-cols-3">
			{children}
		</div>
	);
}

function Step({
	number,
	client,
	step,
	children,
}: {
	number: number;
	client: (typeof CLIENT_KEYS)[number];
	step: "step1" | "step2" | "step3";
	children?: ReactNode;
}) {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-3 bg-base-100 p-4 sm:p-5">
			<span className="flex h-7 w-7 items-center justify-center rounded-full bg-base-200 font-display font-bold text-base-content/70 text-sm">
				{number}
			</span>
			<div className="flex flex-col gap-1.5">
				<h3 className="font-display font-bold text-base text-base-content">
					{t(`mcp.connect.${client}.${step}.title`)}
				</h3>
				<p className="text-base-content/70 text-sm leading-relaxed">
					{t(`mcp.connect.${client}.${step}.description`)}
				</p>
			</div>
			{children && <div className="mt-auto pt-2">{children}</div>}
		</div>
	);
}

/** Single-line copyable artifact — the address, or the Claude Code command. */
function CopyField({
	value,
	copied,
	onCopy,
}: {
	value: string;
	copied: boolean;
	onCopy: () => void;
}) {
	const { t } = useTranslation();

	return (
		<div className="flex items-center gap-1 rounded-lg border border-base-300 bg-base-200 py-1 pr-1 pl-3">
			<code className="min-w-0 flex-1 truncate font-mono text-base-content text-xs">
				{value}
			</code>
			<Button
				type="button"
				variant="ghost"
				size="icon"
				className="size-8 shrink-0"
				onClick={onCopy}
				aria-label={copied ? t("mcp.url.copied") : t("mcp.url.copy")}
			>
				{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
			</Button>
		</div>
	);
}

/**
 * A menu path rendered as chips — "Settings › Connectors › …". Chevrons are
 * drawn, never typed, so screen readers get the steps and not an arrow glyph.
 */
function PathChips({ steps }: { steps: string[] }) {
	return (
		// The chevron trails its own chip rather than leading the next one: on a
		// phone the row wraps, and a leading chevron landed alone at the start of
		// the second line reading as a stray arrow.
		<span className="flex flex-wrap items-center gap-y-1.5">
			{steps.map((step, index) => (
				<span key={step} className="flex items-center">
					<span className="rounded-md border border-base-300 bg-base-100 px-2.5 py-0.5 font-display font-semibold text-base-content text-sm">
						{step}
					</span>
					{index < steps.length - 1 && (
						<ChevronRight
							aria-hidden
							className="mx-1 h-4 w-4 shrink-0 text-base-content/35"
							strokeWidth={1.75}
						/>
					)}
				</span>
			))}
		</span>
	);
}

function CodeBlock({
	label,
	value,
	copied,
	onCopy,
}: {
	label: string;
	value: string;
	copied: boolean;
	onCopy: () => void;
}) {
	const { t } = useTranslation();

	return (
		<div className="overflow-hidden rounded-lg border border-base-300 bg-base-100">
			<div className="flex items-center justify-between gap-2 border-base-300 border-b bg-base-200 py-1.5 pr-2 pl-3 sm:pl-4">
				<span className="font-display font-semibold text-base-content/60 text-sm">
					{label}
				</span>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={onCopy}
					aria-label={copied ? t("mcp.url.copied") : t("mcp.url.copy")}
				>
					{copied ? (
						<Check className="h-4 w-4" />
					) : (
						<Copy className="h-4 w-4" />
					)}
				</Button>
			</div>
			<pre className="overflow-x-auto p-3 font-mono text-base-content text-xs leading-relaxed sm:p-4 sm:text-sm">
				{value}
			</pre>
		</div>
	);
}

function PermissionCard({
	icon,
	title,
	children,
}: {
	icon: ReactNode;
	title: string;
	children: ReactNode;
}) {
	return (
		<section className="flex flex-col gap-4 rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm sm:p-6">
			{/* Both titles wrap to two lines on a phone; centring floated the icon
			    between them. It sits on the first line instead. */}
			<div className="flex items-start gap-2.5">
				<span className="mt-0.5 shrink-0">{icon}</span>
				<h3 className="font-display font-bold text-base-content text-lg">
					{title}
				</h3>
			</div>
			<ul className="flex flex-col gap-3 text-base-content/90 text-sm leading-relaxed">
				{children}
			</ul>
		</section>
	);
}
